import { useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useDatasetAtoms, useDatasetConfig } from '../dataset-context';
import { setDatasetTextAtom } from '../atoms';
import type { MapQueryExport } from '../use-map-query-export';
import type { Bounds, RowData } from './types';
import type { ExpandAllBroadcast } from './utils';

const PREVIEW_SLIDE_OUT_MS = 240; // matches viewer-slide-out in results-section.module.scss

/**
 * A document card's open state: whether it's expanded (and ever has been, so
 * its body mounts once and stays), plus the open signals its sections read.
 * Reacts to expand-all and prev/next-open CHANGES only, never to a signal
 * already set when the card mounts: otherwise cards mounted after an Expand-All
 * (pagination, a new search re-mounting rows) auto-expand and fire their lazy
 * GETs unasked.
 * @param {ExpandAllBroadcast | undefined} expandAll The expand/collapse-all broadcast.
 * @param {ExpandAllBroadcast | undefined} navOpen The prev/next open pulse.
 * @return {object} The open state and its togglers.
 */
export const useCardOpenState = (
  expandAll: ExpandAllBroadcast | undefined,
  navOpen: ExpandAllBroadcast | undefined
) => {
  const [expanded, setExpanded] = useState(false);
  const [everExpanded, setEverExpanded] = useState(false);
  // Targeted open signal for the Parties accordion: a parties "Show more" click
  // expands the card AND opens the Parties accordion (not just toggles the card).
  // It mirrors expand-all so that control still drives the Parties accordion too.
  const [partiesSignal, setPartiesSignal] = useState<
    ExpandAllBroadcast | undefined
  >(undefined);
  // The card body's open signal, fed by BOTH the global expand/collapse-all and
  // the per-card prev/next open pulse. Every sub-section reads this (not the raw
  // expandAll) so a nav-open opens all sections, yet a later global collapse-all
  // still wins — whichever fired most recently sets it.
  const [sectionSignal, setSectionSignal] = useState<
    ExpandAllBroadcast | undefined
  >(undefined);
  const [seenExpandAll, setSeenExpandAll] = useState(expandAll);
  const [seenNavOpen, setSeenNavOpen] = useState(navOpen);

  if (expandAll !== seenExpandAll) {
    setSeenExpandAll(expandAll);
    if (expandAll) {
      setExpanded(expandAll.open);
      setSectionSignal(expandAll);
      setPartiesSignal(expandAll);
    }
  }
  // Prev/next navigation opens the navigated card and all its sections — same
  // as expand-all, but scoped to this one card.
  if (navOpen !== seenNavOpen) {
    setSeenNavOpen(navOpen);
    if (navOpen) {
      const open = { open: true };
      setExpanded(true);
      setSectionSignal(open);
      setPartiesSignal(open);
    }
  }
  if (expanded && !everExpanded) setEverExpanded(true);

  /** Expands or collapses the card. */
  const toggleExpand = () => setExpanded((v) => !v);
  /** Expands the card with its Parties section open. */
  const openParties = () => {
    setExpanded(true);
    setPartiesSignal({ open: true });
  };

  return {
    expanded,
    everExpanded,
    sectionSignal,
    partiesSignal,
    toggleExpand,
    openParties,
  };
};

/**
 * Commits the dataset's own search text (combined with the global text).
 * @return {Function} Sets the dataset's search text.
 */
export const useDatasetSearchChange = () => {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const setDatasetText = useSetAtom(setDatasetTextAtom);
  return useCallback(
    (value: string) => {
      setDatasetText({ atoms, config, value });
    },
    [setDatasetText, atoms, config]
  );
};

/**
 * Selects the highlighted item and its footprint bounds together so the two can
 * never drift: the highlight-only tile source is pinned to this envelope, and a
 * stale bounds would leave the footprint pointing at the wrong feature. Both
 * selection paths (table "Show on Map" and card select) route through here.
 * @return {Function} Selects an item (or clears with nulls).
 */
export const useSelectHighlighted = () => {
  const atoms = useDatasetAtoms();
  const setHighlightedItem = useSetAtom(atoms.highlightedItemIdAtom);
  const setHighlightedBounds = useSetAtom(atoms.highlightedItemBoundsAtom);
  return useCallback(
    (id: string | null, bounds: Bounds | null) => {
      setHighlightedItem(id);
      setHighlightedBounds(bounds);
    },
    [setHighlightedItem, setHighlightedBounds]
  );
};

/**
 * The dataset's CSV export of the applied search, run by the page (one export
 * at a time; its status banner sits in the tab bar), and why it can't start.
 * @param {MapQueryExport} mapQueryExport The page's CSV export.
 * @return {object} The export trigger, its disabled reason and status.
 */
export const useDatasetExport = (mapQueryExport: MapQueryExport) => {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const { loading, currentPayload, error } = useAtomValue(atoms.searchAtom);
  const { startExport, isExporting, exportLabel, userId } = mapQueryExport;
  const canExport = Boolean(currentPayload && userId);
  /**
   * Starts the dataset's CSV export of the applied search.
   * @param {string[]} [columns] The visible column ids, in order (table view).
   * @return {Promise<void>} Settles once the export is queued.
   */
  const exportDataset = (columns?: string[]) =>
    startExport({
      endpoint: config.searchEndpoint,
      label: config.label,
      payload: currentPayload,
      columns,
    });
  // A search sets currentPayload before it runs and keeps it if it fails, while
  // the last rows stay on screen, so only export once that payload has loaded.
  let disabledReason: string | undefined;
  if (isExporting) disabledReason = 'An export is already running';
  else if (loading) disabledReason = 'Results are loading';
  else if (error) disabledReason = 'Refresh the results to export';
  else if (!canExport) disabledReason = 'Nothing to export yet';
  return {
    exportDataset,
    disabledReason,
    exporting: isExporting && exportLabel === config.label,
  };
};

/**
 * Prev/next navigation through the runsheet cards and the document preview
 * that travels with it. When the preview is open, both the toolbar and the
 * viewer chevrons move it and the active card together (anchored on the
 * previewed doc) so the list and viewer never drift apart.
 * @param {RowData[]} rows The loaded rows.
 * @return {object} The navigation and preview state.
 */
export const useRunsheetNavigation = (rows: RowData[]) => {
  const cardScrollRef = useRef<HTMLDivElement>(null);
  // A mirror of the current rows so the stable callbacks below can read them
  // without being re-created per page (which would defeat card memo).
  const rowsRef = useRef<RowData[]>(rows);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  // Which card a prev/next click should open, with a fresh signal each click so
  // the target card re-opens even when navigated to repeatedly. Set only by
  // navigation (not a plain card click), so clicking a card doesn't force it open.
  const [navTarget, setNavTarget] = useState<{
    index: number;
    open: ExpandAllBroadcast;
  }>();
  const [previewId, setPreviewId] = useState<number | undefined>();
  // The preview slides in on mount (CSS) and slides out on close. Keep it
  // mounted for the slide-out duration after `previewId` clears, then unmount.
  const [shownPreviewId, setShownPreviewId] = useState<number | undefined>();
  if (previewId !== undefined && shownPreviewId !== previewId) {
    setShownPreviewId(previewId);
  }
  useEffect(() => {
    if (previewId !== undefined) return undefined;
    const timer = window.setTimeout(
      () => setShownPreviewId(undefined),
      PREVIEW_SLIDE_OUT_MS
    );
    return () => window.clearTimeout(timer);
  }, [previewId]);

  // New results (search/sort/page) reset the active card to the top of the
  // list. Keep the preview open when its doc survives the refetch (a map nudge,
  // divider drag or first keystroke can re-fetch with it still listed); only
  // close it when the previewed row is gone.
  const [seenRows, setSeenRows] = useState(rows);
  if (rows !== seenRows) {
    setSeenRows(rows);
    setActiveCardIndex(0);
    setNavTarget(undefined);
    if (
      previewId !== undefined &&
      !rows.some((r) => Number(r.id) === previewId)
    ) {
      setPreviewId(undefined);
    }
  }

  const scrollToCard = useCallback((index: number) => {
    const count = rowsRef.current.length;
    if (count === 0) return;
    const clamped = Math.max(0, Math.min(index, count - 1));
    setActiveCardIndex(clamped);
    setNavTarget({ index: clamped, open: { open: true } });
    cardScrollRef.current?.children[clamped]?.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
  }, []);
  const togglePreview = useCallback((id: number) => {
    setPreviewId((current) => (current === id ? undefined : id));
    // Make the previewed card the active one so the highlight and prev/next
    // anchor track the viewer from the moment it opens.
    const idx = rowsRef.current.findIndex((r) => Number(r.id) === id);
    if (idx >= 0) setActiveCardIndex(idx);
  }, []);
  /**
   * Makes the card for a row id the active one for prev/next.
   * @param {number | string} id The row's id.
   */
  const activateCard = useCallback((id: number | string) => {
    const idx = rowsRef.current.findIndex((r) => String(r.id) === String(id));
    if (idx >= 0) setActiveCardIndex(idx);
  }, []);

  // During the slide-out, `previewId` is cleared but the last document stays
  // shown.
  const activePreviewId = previewId ?? shownPreviewId;
  const previewRow =
    activePreviewId !== undefined
      ? rows.find((r) => Number(r.id) === activePreviewId)
      : undefined;
  const previewIndex = previewRow ? rows.indexOf(previewRow) : -1;
  const navAnchor =
    previewId !== undefined && previewIndex >= 0
      ? previewIndex
      : activeCardIndex;
  /**
   * Steps to a neighbouring document, moving the preview with it when open.
   * @param {number} offset How many documents to step (negative is back).
   */
  const navigate = (offset: number) => {
    const target = navAnchor + offset;
    if (target < 0 || target >= rows.length) return;
    scrollToCard(target);
    if (previewId !== undefined) setPreviewId(Number(rows[target].id));
  };

  return {
    cardScrollRef,
    activeCardIndex,
    activateCard,
    navTarget,
    previewId,
    setPreviewId,
    shownPreviewId,
    activePreviewId,
    previewRow,
    togglePreview,
    navigate,
    navAnchor,
  };
};
